import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { get12HourTime, getDateString } from "../../common/utils";
import { GRAY_300, GRAY_400 } from "../../common/styles/colors";
import {
    SPACING_2,
    SPACING_8,
    SPACING_16,
    SPACING_20,
    SPACING_24,
    SPACING_64,
    SPACING_60,
} from "../../common/styles/spacing";
import useRAF from "../../common/hooks/useRAF";
import {
    FONT_0_5,
    FONT_0_875,
    FONT_1_25,
    HEADING_FONT,
} from "../../common/styles/fonts";

const Clock = styled.div`
  display: flex;
  border-radius: ${SPACING_24};
  padding: ${SPACING_8} ${SPACING_16};
  align-items: center;

  background: ${GRAY_300};
`;

const StyledHeading = styled.span`
  font-family: ${HEADING_FONT};
  font-size: ${FONT_0_5};
  font-weight: 600;

  color: ${GRAY_400};

  word-break: break-word;
  text-align: right;
  max-width: ${SPACING_64};

  text-transform: uppercase;

  margin: 0 ${SPACING_16};
`;

const StyledTime = styled.time`
  font-family: ${HEADING_FONT};
  font-size: ${FONT_1_25};
  font-weight: bold;
  text-align: right;

  margin-left: ${SPACING_2};

  min-width: ${SPACING_60};
`;

const StyledDate = styled.span`
  font-family: ${HEADING_FONT};
  font-size: ${FONT_0_875};
  margin-left: ${SPACING_20};
`;

export default ({
    className = "",
    timezone,
}: {
  className?: string;
  timezone?: string;
}) => {
    const { t } = useTranslation();

    // The reason we are putting timezone in state is because
    // useRAF is not able to read in timezone from the props due to some scoping
    const [localTime, setLocalTime] = useState(get12HourTime(new Date()));
    const [siteTime, setSiteTime] = useState(get12HourTime(new Date(), timezone));
    const [siteDate, setSiteDate] = useState(getDateString(new Date(), timezone));

    const updateTime = () => {
        const newDate = new Date();
        setLocalTime(get12HourTime(newDate));
        setSiteTime(get12HourTime(newDate, timezone));
        setSiteDate(getDateString(newDate, timezone));
    };

    /* This is actually fairly performant as react will not update
   * if the state values are the same, thus around 60 times per second
   * this hook will be called, but react will only update this component
   * every minute.
   * In general requestAnimationFrame is actually more performant than setTimeout
   * or setInterval
   *
   * This hook will get recreated whenever the timezone changes
   */
    useRAF(() => {
        updateTime();
    }, [timezone]);

    return (
        <Clock className={className}>
            <StyledHeading>{t("header.localTime")}</StyledHeading>
            <StyledTime dateTime={new Date().toISOString()}>{localTime}</StyledTime>
            <StyledHeading>{t("header.siteTime")}</StyledHeading>
            <StyledTime dateTime={new Date().toISOString()}>{siteTime}</StyledTime>
            <StyledDate>{siteDate}</StyledDate>
        </Clock>
    );
};
